import { useState } from "react";
import { AlertCircle, AlertTriangle, Info, X, ChevronRight, Check } from "lucide-react";
import { Drawer } from "@/components/system";
import { useTodayRules, type PersistedFiring } from "@/hooks/use-today-rules";
import { useEvidenceByIds } from "@/hooks/use-evidence";
import type { RuleSeverity } from "@/rules/types";

/**
 * Renders fired Rules between Greeting and CountdownBand on Today.
 * - 'bad' (critical) cannot be dismissed locally; only acknowledged later
 * - 'warn' renders amber; 'neutral'/'good' render in info style
 * - "Why?" link opens a drawer with the citation chain for any cited Evidence
 *
 * Local dismissal lives in component state for the v1 (writes to Supabase
 * `rule_firing.dismissed` once `runRulesForUser` persists firings — wired in
 * BW9 alongside the daily Worker run).
 */

const SEV_STYLE: Record<
  RuleSeverity,
  { tint: string; ring: string; icon: typeof Info }
> = {
  bad: { tint: "rgba(199,107,107,0.10)", ring: "var(--brick)", icon: AlertCircle },
  warn: { tint: "rgba(200,155,92,0.10)", ring: "var(--amber)", icon: AlertTriangle },
  neutral: { tint: "var(--chip)", ring: "var(--hair-2)", icon: Info },
  good: { tint: "var(--accent-wash)", ring: "var(--accent-dim)", icon: Info },
};

export function AlertBanner() {
  const { firings, loading, dismiss, acknowledge } = useTodayRules();
  const [openFiring, setOpenFiring] = useState<PersistedFiring | null>(null);

  if (loading) return null;
  if (firings.length === 0) return null;

  return (
    <section aria-label="Active alerts" style={{ display: "grid", gap: "var(--space-2)" }}>
      {firings.map((f) => (
        <BannerRow
          key={f.firing_id}
          firing={f}
          onDismiss={() => {
            if (f.severity === "bad") return;
            dismiss(f.firing_id);
          }}
          onAcknowledge={() => acknowledge(f.firing_id)}
          onOpenWhy={() => setOpenFiring(f)}
        />
      ))}
      <WhyDrawer firing={openFiring} onClose={() => setOpenFiring(null)} />
    </section>
  );
}

function BannerRow({
  firing,
  onDismiss,
  onAcknowledge,
  onOpenWhy,
}: {
  firing: PersistedFiring;
  onDismiss: () => void;
  onAcknowledge: () => void;
  onOpenWhy: () => void;
}) {
  const style = SEV_STYLE[firing.severity];
  const Icon = style.icon;
  const dismissable = firing.severity !== "bad";
  const acknowledged = firing.acknowledged_at != null;

  return (
    <article
      role="alert"
      style={{
        background: style.tint,
        border: `1px solid ${style.ring}`,
        borderRadius: "var(--radius-card)",
        padding: "var(--space-3) var(--space-4)",
        display: "grid",
        gridTemplateColumns: "20px 1fr auto",
        alignItems: "start",
        gap: "var(--space-3)",
        color: "var(--fg)",
      }}
    >
      <Icon
        size={18}
        strokeWidth={1.6}
        aria-hidden
        style={{ color: style.ring, marginTop: 2 }}
      />
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: "var(--type-body-s)",
            lineHeight: 1.45,
            color: "var(--fg)",
          }}
        >
          {firing.message}
        </p>
        {firing.evidence_refs.length > 0 && (
          <button
            type="button"
            onClick={onOpenWhy}
            style={{
              marginTop: 4,
              padding: 0,
              background: "transparent",
              border: "none",
              color: "var(--accent)",
              fontSize: "var(--type-meta)",
              fontWeight: 500,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            Why? <ChevronRight size={12} strokeWidth={1.8} />
          </button>
        )}
      </div>
      {dismissable ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss alert"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--fg-3)",
            cursor: "pointer",
            padding: 4,
            marginTop: -2,
            marginRight: -4,
          }}
        >
          <X size={14} strokeWidth={1.6} />
        </button>
      ) : !acknowledged ? (
        <button
          type="button"
          onClick={onAcknowledge}
          aria-label="Acknowledge alert"
          title="Acknowledge"
          style={{
            background: "transparent",
            border: `1px solid ${style.ring}`,
            borderRadius: "var(--radius-chip)",
            color: style.ring,
            cursor: "pointer",
            padding: "2px 8px",
            fontSize: "var(--type-meta)",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Check size={11} strokeWidth={2} /> Ack
        </button>
      ) : (
        <span
          aria-label="Acknowledged"
          style={{
            color: "var(--fg-3)",
            fontSize: "var(--type-meta)",
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Check size={11} strokeWidth={2} /> Ack’d
        </span>
      )}
    </article>
  );
}

function WhyDrawer({
  firing,
  onClose,
}: {
  firing: PersistedFiring | null;
  onClose: () => void;
}) {
  const ids = firing?.evidence_refs ?? [];
  const q = useEvidenceByIds(ids);

  return (
    <Drawer
      open={firing != null}
      onClose={onClose}
      title={firing ? "Why this fired" : ""}
    >
      {firing && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)", lineHeight: 1.55 }}>
            {firing.message}
          </p>

          {firing.factors.length > 0 && (
            <section>
              <h4
                style={{
                  fontSize: "var(--type-meta)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--fg-3)",
                  margin: "0 0 var(--space-2)",
                  fontWeight: 500,
                }}
              >
                Factors
              </h4>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
                {firing.factors.map((f) => (
                  <li
                    key={f.name}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "var(--space-2)",
                      fontSize: "var(--type-body-s)",
                      color: "var(--fg-2)",
                    }}
                  >
                    <span>{f.name.replace(/_/g, " ")}</span>
                    <span className="mono" style={{ color: "var(--fg)" }}>
                      {f.value}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h4
              style={{
                fontSize: "var(--type-meta)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
                margin: "0 0 var(--space-2)",
                fontWeight: 500,
              }}
            >
              Evidence
            </h4>
            {q.isLoading ? (
              <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>Loading…</p>
            ) : (q.data ?? []).length === 0 ? (
              <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
                No matching evidence found in your library.
              </p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 12 }}>
                {(q.data ?? []).map((ev) => (
                  <li
                    key={ev.id}
                    style={{
                      borderLeft: "2px solid var(--accent)",
                      paddingLeft: "var(--space-3)",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: "var(--type-body-s)",
                        lineHeight: 1.5,
                        color: "var(--fg)",
                      }}
                    >
                      {ev.claim}
                    </p>
                    {ev.paper && (
                      <p
                        style={{
                          margin: "4px 0 0",
                          fontSize: "var(--type-meta)",
                          color: "var(--fg-3)",
                        }}
                      >
                        {ev.paper.handle}
                        {ev.paper.journal ? ` · ${ev.paper.journal}` : ""}
                        {ev.paper.year ? ` · ${ev.paper.year}` : ""}
                        {ev.paper.doi && (
                          <>
                            {" · "}
                            <a
                              href={`https://doi.org/${ev.paper.doi}`}
                              target="_blank"
                              rel="noreferrer noopener"
                              style={{ color: "var(--accent)" }}
                            >
                              doi
                            </a>
                          </>
                        )}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}
